import { IconMark } from "../primitives/IconMark";
import { Icon } from "../icons/Icon";
import { Button } from "../primitives/Button";
import { HelpIcon } from "../primitives/Help";
import { cx } from "../utils/format";

/* M12: the public API's pieces. A site (a WordPress installation, a script) connects to a workspace; its tickets, its replies, the
 * addresses it tells us and what it knows about a customer show in these. Props in, events out; every word the app formats. */

/** "Public on forum.example.com": a forum topic everyone reads. Beside the ticket's channel in its band. */
/** The site's pill in the ticket's band: "Public on forum.kriesi.at" (everyone there reads the thread) or "Via forum.kriesi.at"; with
 *  `href` it opens the thread on the site in a new tab and ends with the open-in-new mark (the founder, 2026-10-11: one pill, not a
 *  channel label, a pill and a "View on site" link). */
export function SiteMark({ host, href, isPublic = true, iconOnly = false }: { host: string; /** the thread on the site */ href?: string; isPublic?: boolean; /** the founder (2026-10-27): on the ticket's first message the mark is the web icon alone, "Via <host>" / "Public on <host>" as its tooltip and spoken name, the same scheme as the channel icon */ iconOnly?: boolean }) {
  if (iconOnly) return <IconMark icon="web" label={`${isPublic ? "Public on" : "Via"} ${host}`} href={href} className="ob-entry__mark ob-site-mark--icon" />;
  const body = <><Icon name="web" size="xs" />{isPublic ? "Public on" : "Via"} {host}{href && <Icon name="openInNew" size="xs" />}</>;
  const title = isPublic ? "Everyone on the forum reads the replies" : `Filed by ${host}`;
  return href
    ? <a className="ob-site-mark" href={href} target="_blank" rel="noopener noreferrer" title={`${title} · open on ${host}`}>{body}</a>
    : <span className="ob-site-mark" title={title}>{body}</span>;
}

/** what the help icon says on a public topic, by the site's mode for public topics (M14 round five; absent: the default, by draft) */
export const PUBLIC_REPLY_HELP = {
  draft: "This site answers public topics by draft: the AI used the customer's facts, nothing goes out until you send.",
  auto: "This site answers public topics automatically: the AI had no customer facts.",
} as const;

/** Where a reply goes: the one line above the composer and the reply bar (the founder, 2026-10-11: one short line, not two). On a
 *  public topic: "Public forum reply on forum.example.com", with what the site's mode means behind a help icon (M14 round five: by draft,
 *  the AI used the customer's facts and nothing goes out until a person sends; automatically, the AI had no customer facts). To a site's
 *  message centre: "Reply goes to the customer on host". By mail: "Reply by mail to address · cc …". `host` is the site's label ("host" or
 *  "host/folder"). */
export function ReplyRoute({ via, host, url, where, address, cc, isPublic, publicReplies = "draft" }: { via: "site" | "mail"; host?: string; /** the thread on the site: the site's name links to it, in a new tab */ url?: string; /** "forum", "message centre"; absent: "site" */ where?: string; /** by mail: to whom */ address?: string; /** by mail: the copied people */ cc?: string[]; isPublic?: boolean; /** how the ticket's site answers public topics */ publicReplies?: "draft" | "auto" }) {
  const site = url ? <a className="ob-reply-route__site" href={url} target="_blank" rel="noopener noreferrer"><strong>{host}</strong></a> : <strong>{host}</strong>;
  return (
    <div className={cx("ob-reply-route", isPublic && "ob-reply-route--public")} role="note">
      <Icon name={via === "site" ? "web" : "mail"} size="sm" />
      <span className="ob-reply-route__text">
        {via === "site" && isPublic && <>Public forum reply on {site}</>}
        {via === "site" && !isPublic && <>Reply goes to the customer on {site}{where ? <> ({where})</> : null}</>}
        {via === "mail" && <>Reply by mail{address ? <> to <strong>{address}</strong></> : null}{cc?.length ? <span className="ob-reply-route__cc"> · cc {cc.join(", ")}</span> : null}</>}
        {isPublic && <HelpIcon label="What a public reply means" text={`Everyone on the forum reads it. ${PUBLIC_REPLY_HELP[publicReplies]}`} />}
      </span>
    </div>
  );
}

export interface AddressRowData { value: string; kind: "email" | "external"; primary: boolean; confirmed: boolean; /** the site that told us; absent: the customer mailed us from it */ addedBy?: string; }
/** The customer's addresses on the card: the primary first (a site never makes an address primary), then the secondary ones, then the
 *  ids sites know the person by. Which site added which, and "Unconfirmed" until the person proved it is theirs. */
export function CustomerAddresses({ rows }: { rows: AddressRowData[] }) {
  return (
    <ul className="ob-addresses" aria-label="Addresses">
      {rows.map((r) => (
        <li key={`${r.kind}:${r.addedBy ?? ""}:${r.value}`} className={cx("ob-addresses__row", r.primary && "ob-addresses__row--primary")}>
          <Icon name={r.kind === "email" ? "mail" : "user"} size="sm" className="ob-addresses__icon" />
          <span className="ob-addresses__text">
            <span className="ob-addresses__value">{r.kind === "external" ? `User ${r.value} on ${r.addedBy}` : r.value}</span>
            <span className="ob-addresses__meta">
              {r.primary && <span className="ob-addresses__tag ob-addresses__tag--primary">Primary</span>}
              {r.kind === "email" && r.addedBy && <span className="ob-addresses__tag">Added by {r.addedBy}</span>}
              {r.kind === "email" && !r.confirmed && <span className="ob-addresses__tag ob-addresses__tag--unconfirmed">Unconfirmed</span>}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/** What a key may do: one line per scope, its words and its code. */
export function ScopeList({ label, items }: { label: string; items: { id: string; label: string }[] }) {
  return <ul className="ob-scope-list" aria-label={label}>{items.map((s) => <li key={s.id} className="ob-scope-list__item"><Icon name="check" size="sm" /><span>{s.label}</span><code className="ob-scope-list__code">{s.id}</code></li>)}</ul>;
}

/** The site's message centre (M13's plugin renders it with this look): a signed-in visitor sees the tickets they made on the site at
 *  once, and their earlier mail conversations only after confirming the address by a link we mail to it. */
export function PortalConfirm({ email, sent, onSend }: { email: string; /** the link is on its way */ sent?: boolean; onSend?: () => void }) {
  return (
    <section className="ob-portal-confirm" aria-label="Confirm your email">
      <Icon name="mail" className="ob-portal-confirm__icon" />
      <div className="ob-portal-confirm__text">
        <h3 className="ob-portal-confirm__title">{sent ? "Check your inbox" : "Confirm your email to see your earlier conversations"}</h3>
        <p className="ob-portal-confirm__line">{sent ? <>We sent a link to <strong>{email}</strong>. It works once, for 24 hours.</> : <>We send a link to <strong>{email}</strong>. The tickets you wrote here show already.</>}</p>
      </div>
      {!sent && <Button size="sm" variant="primary" onClick={onSend}>Send the link</Button>}
    </section>
  );
}
